import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useResourcesList } from "../../ResourcesList/resourcesList";
import { isResourcesListItemSelected } from "../../Utils/resourcesList";
import { MAP_ITEM_TYPE_ELEMENT, useMapContext } from "../MapContext";
import { selectItem, unselectItem } from "../../../reducers/resourcesList/selection";

import {
  hideInformationWindow,
  showInformationWindow
} from "../../../reducers/map/informationWindow";

const MapElementsVectorGridClickHandler = ({
  setClickedMapElementGeometry,
  mapElementGeometry
}) => {
  const { id: mapElementGeometryId, hasInformationWindow } = mapElementGeometry;
  const { mapKey, registerMapClickedHandler, unregisterMapClickedHandler } =
    useMapContext();
  const resourcesList = useResourcesList();
  const { resourcesListKey } = resourcesList;

  const isSelected = isResourcesListItemSelected(resourcesList, {
    mapElementGeometryId,
    type: MAP_ITEM_TYPE_ELEMENT
  });

  const currentInformationWindowId = useSelector(
    ({ map }) => map[mapKey]?.informationWindow?.mapElementGeometryId
  );

  const informationWindowWasOpen = useRef(false);

  const dispatch = useDispatch();

  // Close the information window and unselect the current item when the map is clicked.
  const handleMapClicked = useCallback(() => {
    if (currentInformationWindowId === mapElementGeometryId) {
      dispatch(hideInformationWindow(mapKey));
    }

    if (isSelected && mapElementGeometryId) {
      dispatch(unselectItem(resourcesListKey));
    }
  }, [
    currentInformationWindowId,
    dispatch,
    isSelected,
    mapElementGeometryId,
    mapKey,
    resourcesListKey
  ]);

  // While an information window is open or an item is selected, add a callback to the map
  // click event to allow closing the information window and unselecting the item when
  // a random location on the map is clicked.
  useEffect(() => {
    if (!mapElementGeometryId) return;

    registerMapClickedHandler(handleMapClicked);

    return () => unregisterMapClickedHandler(handleMapClicked);
  }, [
    registerMapClickedHandler,
    unregisterMapClickedHandler,
    mapElementGeometryId,
    handleMapClicked
  ]);

  // Force unsetting the current clicked map element geometry when the information window
  // was previously open and is closed. This allows handling every case of closing the
  // information window, whether it's closed from the info window back button, or by
  // clicking on the map when it's open.
  useEffect(() => {
    if (
      !mapElementGeometryId ||
      currentInformationWindowId ||
      !informationWindowWasOpen.current
    ) {
      return;
    }

    setClickedMapElementGeometry(null);
  }, [currentInformationWindowId, mapElementGeometryId, setClickedMapElementGeometry]);

  // When this component is mounted, show the information window if the map element
  // geometry has an information window, and select the item in the resources list.
  useEffect(() => {
    if (mapElementGeometryId && hasInformationWindow) {
      dispatch(showInformationWindow(mapKey, mapElementGeometryId));
      informationWindowWasOpen.current = true;
    }

    dispatch(selectItem(resourcesListKey, mapElementGeometryId, MAP_ITEM_TYPE_ELEMENT));
  }, [dispatch, hasInformationWindow, mapElementGeometryId, mapKey, resourcesListKey]);

  return null;
};

export default MapElementsVectorGridClickHandler;
